import { mediaUrl } from "./sequences/reelMedia";
/**
 * Turn a destination's own photographs into the cards the map fan shows.
 *
 * The fan used a pool of six bundled PNGs cycled by index, so it never showed the trip's pictures --
 * a Ginza crawl fanned out palm trees. The card's look, though, lives in those PNGs: the rounded
 * corner, the white border, the drop shadow. Pointing `icon-image` at a raw photo would give a bare
 * rectangle, so the photo has to be drawn into that shape first.
 */

/** Matches card-1.png, so a photo card and a fallback card are the same size on screen. */
export const CARD_W = 368;
export const CARD_H = 416;
const RADIUS = 34;
const BORDER = 14;

const roundedPath = (ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number) => {
	ctx.beginPath();
	ctx.moveTo(x + r, y);
	ctx.arcTo(x + w, y, x + w, y + h, r);
	ctx.arcTo(x + w, y + h, x, y + h, r);
	ctx.arcTo(x, y + h, x, y, r);
	ctx.arcTo(x, y, x + w, y, r);
	ctx.closePath();
};

/**
 * Draw one photo as a card: white border, rounded corners, the photo cropped to fill rather than
 * squashed -- a portrait dish and a landscape shopfront both have to sit in the same frame.
 */
export const drawPhotoCard = (img: HTMLImageElement): ImageData | null => {
	const canvas = document.createElement("canvas");
	canvas.width = CARD_W;
	canvas.height = CARD_H;
	const ctx = canvas.getContext("2d");
	if (!ctx) return null;

	ctx.fillStyle = "#FFFFFF";
	roundedPath(ctx, 0, 0, CARD_W, CARD_H, RADIUS);
	ctx.fill();

	const [iw, ih] = [CARD_W - BORDER * 2, CARD_H - BORDER * 2];
	ctx.save();
	roundedPath(ctx, BORDER, BORDER, iw, ih, RADIUS - BORDER / 2);
	ctx.clip();
	const scale = Math.max(iw / img.width, ih / img.height);
	const [dw, dh] = [img.width * scale, img.height * scale];
	ctx.drawImage(img, BORDER + (iw - dw) / 2, BORDER + (ih - dh) / 2, dw, dh);
	ctx.restore();

	return ctx.getImageData(0, 0, CARD_W, CARD_H);
};

/** The photos a stop actually contributes to its fan, capped at what the fan can hold. */
export const stopPhotos = (destination: any, max: number): string[] =>
	((destination?.photos as any[]) || [])
		.map((p) => mediaUrl(p))
		.filter(Boolean)
		.slice(0, max);

/** The image name a card layer points at; unique per stop so neighbours never collide. */
export const cardImageName = (stop: number, card: number) => `stopPhoto-${stop}-${card}`;
